import React, { createContext, useContext, useReducer, Reducer, Dispatch, ReducerAction } from 'react';
import { AppState, AppStateActions } from '../../interfaces';

type Action = ReducerAction<Reducer<AppState, AppStateActions>>;

// Initial app state when first mounted
const initialState: AppState = {
  sidePanelOpen: false,
  sidePanelFull: false,
  navBarVisible: false,
  map: {
    activeMarkerSet: 'default',
    activeMarkerType: 'all',
    activeYear: 2019,
    geolocationIntro: true,
    geolocation: 'center',
  },
};

// A reducer function to handle updates to state
const reducer = (state: AppState, action: ReducerAction<Reducer<AppState, AppStateActions>>): AppState => {
  switch (action.type) {
    case 'resetState':
      return {
        ...initialState,
      };
    case 'toggleSidePanel':
      return {
        ...state,
        sidePanelOpen: !state.sidePanelOpen,
      };
    case 'openSidePanel':
      return {
        ...state,
        sidePanelOpen: true,
      };
    case 'closeSidePanel':
      return {
        ...state,
        sidePanelOpen: false,
      };
    case 'setSidePanelFull':
      return {
        ...state,
        sidePanelFull: (action.payload && action.payload.sidePanelFull) || initialState.sidePanelFull,
      };
    case 'setActiveMarkerSet':
      return {
        ...state,
        map: {
          ...state.map,
          activeMarkerSet: (action.payload && action.payload.activeMarkerSet) || 'default',
        },
      };
    case 'setActiveMarkerType':
      return {
        ...state,
        map: {
          ...state.map,
          activeMarkerType:
            action.payload && action.payload.activeMarkerType
              ? action.payload.activeMarkerType
              : initialState.map.activeMarkerType,
        },
      };
    case 'setActiveYear':
      return {
        ...state,
        map: {
          ...state.map,
          activeYear:
            action.payload && action.payload.activeYear ? action.payload.activeYear : initialState.map.activeYear,
        },
      };
    case 'setMapReady': {
      return {
        ...state,
        navBarVisible: true,
        map: {
          ...state.map,
          geolocation: 'top',
          geolocationIntro: false,
        },
      };
    }
    default:
      return state;
  }
};

// Setup the state context
export const StateContext = createContext<[AppState, Dispatch<Action>] | [AppState, () => void]>([
  initialState,
  () => null,
]);

export const StateProvider: React.FC = ({ children }) => (
  <StateContext.Provider value={useReducer(reducer, initialState)}>{children}</StateContext.Provider>
);

// A custom hook that returns the state context
export const useStateValue = () => useContext(StateContext);
